import { useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LeadMap } from './type';
import { CreateLeadPayload, Files, Lead, updateLead } from 'services/leads';
import { useLeads } from 'hooks/useLeads';
import { CATEGORY } from 'const/common';
import { createGoogleMapsDirectionsLink } from 'utils';
import { renderTitleLead } from 'utils/renderTitleLead';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { AddNote } from '../FileDetail/components/LeadBox';
import { useNavigate } from 'react-router-dom';
import { TypeLead, User } from 'types/user';
import { StorageServices } from 'services/storage';
import { adminGetAllUser } from 'services/user';
const storage = new StorageServices();

export const useInjection = ({ isAdmin }: { isAdmin?: boolean }) => {
  const role = storage.getRole();

  const { rows, setRows, formValue, onFilter } = useLeads({
    category: CATEGORY.ADDRESS
  });
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    libraries: ['places'],
    googleMapsApiKey: process.env.REACT_APP_MAP_KEY!
  });
  const { setFlash } = useFlash();
  const navigate = useNavigate();
  const [map, setMap] = useState<null | google.maps.Map>(null);
  const [activeIndex, setActiveIndex] = useState<number[]>([]);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | undefined>();
  const [currentLocation, setCurrentLocation] = useState<undefined | { lat: number; lng: number }>(
    undefined
  );
  const [open, setOpen] = useState<boolean>(false);
  const [leadsMap, setLeadsMap] = useState<LeadMap[]>([]);
  const [center, setCenter] = useState<{ lat: number; lng: number }>();
  const [isOpenModalDetailLead, setIsOpenModalDetailLead] = useState<undefined | Lead>(undefined);

  useEffect(() => {
    if (rows.length > 0) {
      const leadsMap: LeadMap[] = [];
      rows.forEach((item) => {
        leadsMap.push({
          latitude: item.latitude,
          longitude: item.longitude,
          flag: item.flag,
          status: [
            item?.file?.status?.charAt(0)?.toUpperCase() ?? '',
            item?.file?.status?.slice(1)?.toLocaleLowerCase() ?? ''
          ].join('')
        });
      });

      if (leadsMap.length > 0) {
        setLeadsMap(leadsMap);
        setCenter({ lat: leadsMap[0].latitude ?? 0, lng: leadsMap[0].longitude ?? 0 });
      }
    } else {
      setLeadsMap([]);
    }
  }, [rows]);
  useEffect(() => {
    const getCurrentLocation = () => {
      navigator?.geolocation.getCurrentPosition(({ coords: { latitude: lat, longitude: lng } }) => {
        const pos = { lat, lng };

        setCurrentLocation(pos);
      }, getCurrentLocation);
    };
    getCurrentLocation();
  }, []);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      if (center) map.setCenter(center);
      setMap(map);
    },
    [center]
  );

  const handleUpdateLead = useCallback(
    async ({
      idLead,
      payload,
      message
    }: {
      idLead: number;
      payload: Partial<CreateLeadPayload>;
      message: string;
    }) => {
      const data = await updateLead(idLead, {
        ...payload,
        fileId: Number(isOpenModalDetailLead?.fileId)
      });
      setRows((prevState) =>
        prevState.map((item) => {
          if (Number(item.id) === Number(idLead)) {
            return { ...data.lead, file: item.file };
          }
          return item;
        })
      );
      setIsOpenModalDetailLead((prevState) => ({ ...data.lead, file: prevState?.file }));
      setFlash({ type: 'success', message });
    },
    [isOpenModalDetailLead?.fileId, setFlash, setRows]
  );
  const { asyncCallback: asyncUpdateLead } = useAsyncCallback(handleUpdateLead, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const onGetDirection = useCallback(
    (newActiveIndex?: number[]) => {
      const _activeIndex = Array.isArray(newActiveIndex) ? newActiveIndex : activeIndex;
      if (_activeIndex.length) {
        const directionsService = new google.maps.DirectionsService();
        if (!currentLocation) return;
        const origin = currentLocation;

        const waypoints = _activeIndex.map((item) => ({
          location: { lat: leadsMap[item].latitude, lng: leadsMap[item].longitude },
          stopover: true
        }));
        const coordinates = [currentLocation, ...waypoints.map((item) => item.location)];
        const mapsLink = createGoogleMapsDirectionsLink(coordinates);
        window.open(mapsLink, 'blank');
        directionsService.route(
          {
            origin: origin,
            destination: waypoints[waypoints.length - 1]?.location,
            waypoints,
            travelMode: google.maps.TravelMode.DRIVING,
            optimizeWaypoints: true
          },
          (result, status) => {
            if (status === google.maps.DirectionsStatus.OK && result) {
              setDirections(result);
            } else {
              console.error(`error fetching directions ${result}`);
            }
          }
        );
        setActiveIndex([]);
      } else {
        setDirections(undefined);
      }
    },
    [currentLocation, activeIndex]
  );
  const [optionsUser, setOptionsUser] = useState<{ label: string; value: string }[]>([]);

  const getUserBailiff = useCallback(async () => {
    if (!isAdmin) return;
    const data = await adminGetAllUser({ isActive: true });
    const dataUserConvert = (data.users as User[]).map((item) => ({
      label: `${item.firstName} ${item.lastName} ( ${item.email} )`,
      value: item.id
    }));
    setOptionsUser(dataUserConvert);
  }, [isAdmin]);

  const { asyncCallback: asyncFetchBailiff } = useAsyncCallback(getUserBailiff, []);

  useEffect(() => {
    asyncFetchBailiff();
  }, []);

  return {
    map,
    isOpenModalDetailLead,
    activeIndex,
    isLoaded,
    directions,
    itemMVS: rows?.filter((item) => item.type === TypeLead.MVS),
    rows: useMemo(
      () =>
        rows.map((item) => ({
          ...item.file,
          leadId: item.id,
          leadTitle: renderTitleLead(item, false).trimEnd(),
          address: item.address
        })),
      [rows]
    ) as Files[],
    open,
    currentLocation,
    leadsMap,
    center,
    onGetDirection,
    onLoad,
    onUnmount,
    onFilter,
    formValue,
    onClose: useCallback(() => {
      setOpen(false);
    }, []),
    onOpenModalFilter: useCallback(() => {
      setOpen(true);
    }, []),
    onUpdateActiveIndex: useCallback(
      (index: number) => {
        const hasId = activeIndex.findIndex((item) => item === index);
        if (hasId >= 0) {
          setActiveIndex((prevState) => prevState.filter((item) => item !== index));
          return;
        }
        setActiveIndex((prevState) => [...prevState, index]);
      },
      [activeIndex]
    ),
    onHold: useCallback(
      (index: number) => {
        setIsOpenModalDetailLead(rows[index]);
      },
      [rows]
    ),
    onCloseModalDetailLead: useCallback(() => {
      setIsOpenModalDetailLead(undefined);
    }, []),
    handleDeleteNoteLead: async (idNote: number, idLead: number) =>
      await asyncUpdateLead({
        idLead,
        payload: {
          notes: [{ id: idNote, isDelete: true }]
        },
        message: 'Delete note leads successful'
      }),
    handleEditNote: async (idLead: number, idNote: number, content: string) => {
      await asyncUpdateLead({
        idLead,
        payload: {
          notes: [{ id: idNote, content: content }]
        },
        message: 'Edit note leads successful'
      });
    },
    navigateDetailLead: () => {
      if (role !== 'USER') {
        navigate(
          `/admin/files/${isOpenModalDetailLead?.fileId}?leadId=${isOpenModalDetailLead?.id}`
        );
      } else {
        navigate(`/map/file/${isOpenModalDetailLead?.fileId}?leadId=${isOpenModalDetailLead?.id}`);
      }
    },
    handleAddNewNote: async ({ idLead, textNote }: AddNote) =>
      await asyncUpdateLead({
        idLead,
        payload: {
          notes: [{ content: textNote }]
        },
        message: 'Add note leads successful'
      }),
    optionsUser
  };
};
